import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, Plus, ArrowRight, Send } from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Consultations() {
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState([]);
  const [guestConsultations, setGuestConsultations] = useState([]);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [activeTab, setActiveTab] = useState('registered');
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchConsultations();
  }, []);

  const fetchConsultations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/consultations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConsultations(response.data);
      
      if (user?.role === 'lawyer') {
        const guestResponse = await axios.get(`${API}/guest-consultations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGuestConsultations(guestResponse.data);
      }
    } catch (err) {
      console.error('Error fetching consultations:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/consultations`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      fetchConsultations();
      setFormData({ subject: '', message: '' });
    } catch (err) {
      console.error('Error creating consultation:', err);
    }
  };

  const handleRespond = async (consultationId) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = activeTab === 'guest' 
        ? `${API}/guest-consultations/${consultationId}`
        : `${API}/consultations/${consultationId}`;
      
      await axios.put(
        endpoint,
        { response: responseText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedConsultation(null);
      setResponseText('');
      fetchConsultations();
    } catch (err) {
      console.error('Error responding to consultation:', err);
    }
  };

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
            <Logo size="md" />
            <h1 className="text-2xl font-heading font-bold text-white">الاستشارات</h1>
          </div>
          {user?.role === 'client' && (
            <button
              data-testid="add-consultation-btn"
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all duration-300"
            >
              <Plus className="w-5 h-5" />
              طلب استشارة
            </button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs for Lawyer */}
        {user?.role === 'lawyer' && (
          <div className="mb-6 flex gap-2 border-b border-gray-200">
            <button
              data-testid="tab-registered"
              onClick={() => setActiveTab('registered')}
              className={`px-6 py-3 font-body font-medium transition-all ${
                activeTab === 'registered'
                  ? 'text-gold border-b-2 border-gold'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              استشارات العملاء المسجلين ({consultations.length})
            </button>
            <button
              data-testid="tab-guest"
              onClick={() => setActiveTab('guest')}
              className={`px-6 py-3 font-body font-medium transition-all ${
                activeTab === 'guest'
                  ? 'text-gold border-b-2 border-gold'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              استشارات الضيوف ({guestConsultations.length})
            </button>
          </div>
        )}

        <div className="space-y-4">
          {activeTab === 'registered' && consultations.map((cons) => (
            <div
              key={cons.id}
              className="bg-white rounded-lg p-6 shadow-sm border border-gray-100"
              data-testid={`consultation-card-${cons.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <MessageSquare className="w-8 h-8 text-gold mt-1" />
                  <div className="flex-1">
                    <h3 className="text-lg font-heading font-semibold text-primary mb-2">
                      {cons.subject}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2 font-body">
                      من: {cons.client_name} ({cons.client_email})
                    </p>
                    <p className="text-sm text-gray-700 mb-3 font-body">{cons.message}</p>

                    {cons.response && (
                      <div className="mt-4 p-4 bg-sand rounded-md">
                        <p className="text-sm font-semibold text-primary mb-2 font-body">الرد:</p>
                        <p className="text-sm text-gray-700 font-body">{cons.response}</p>
                      </div>
                    )}

                    {user?.role === 'lawyer' && cons.status === 'pending' && (
                      <button
                        data-testid={`respond-btn-${cons.id}`}
                        onClick={() => setSelectedConsultation(cons.id)}
                        className="mt-4 flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all duration-300"
                      >
                        <Send className="w-4 h-4" />
                        الرد على الاستشارة
                      </button>
                    )}

                    {selectedConsultation === cons.id && (
                      <div className="mt-4">
                        <textarea
                          data-testid="response-textarea"
                          value={responseText}
                          onChange={(e) => setResponseText(e.target.value)}
                          placeholder="اكتب ردك هنا..."
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            data-testid="send-response-btn"
                            onClick={() => handleRespond(cons.id)}
                            className="px-4 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all font-body"
                          >
                            إرسال
                          </button>
                          <button
                            data-testid="cancel-response-btn"
                            onClick={() => {
                              setSelectedConsultation(null);
                              setResponseText('');
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-body"
                          >
                            إلغاء
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-body whitespace-nowrap ${
                    cons.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  {cons.status === 'pending' ? 'معلقة' : 'تم الرد'}
                </span>
              </div>
            </div>
          ))}
          
          {activeTab === 'guest' && guestConsultations.map((cons) => (
            <div
              key={cons.id}
              className="bg-white rounded-lg p-6 shadow-sm border border-gray-100"
              data-testid={`guest-consultation-card-${cons.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <MessageSquare className="w-8 h-8 text-gold mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-body">
                        ضيف
                      </span>
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-primary mb-2">
                      {cons.subject}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2 font-body">
                      من: {cons.full_name} - {cons.phone}
                    </p>
                    <p className="text-sm text-gray-700 mb-3 font-body">{cons.message}</p>

                    {cons.attachments && cons.attachments.length > 0 && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-md">
                        <p className="text-sm font-semibold text-primary mb-2 font-body">المرفقات:</p>
                        <div className="space-y-1">
                          {cons.attachments.map((url, index) => (
                            <a
                              key={index}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-sm text-blue-600 hover:text-blue-800 underline font-body"
                            >
                              مرفق {index + 1}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {cons.response && (
                      <div className="mt-4 p-4 bg-sand rounded-md">
                        <p className="text-sm font-semibold text-primary mb-2 font-body">الرد:</p>
                        <p className="text-sm text-gray-700 font-body">{cons.response}</p>
                      </div>
                    )}

                    {user?.role === 'lawyer' && cons.status === 'pending' && (
                      <button
                        data-testid={`respond-btn-${cons.id}`}
                        onClick={() => setSelectedConsultation(cons.id)}
                        className="mt-4 flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all duration-300"
                      >
                        <Send className="w-4 h-4" />
                        الرد على الاستشارة
                      </button>
                    )}

                    {selectedConsultation === cons.id && (
                      <div className="mt-4">
                        <textarea
                          data-testid="response-textarea"
                          value={responseText}
                          onChange={(e) => setResponseText(e.target.value)}
                          placeholder="اكتب ردك هنا..."
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            data-testid="send-response-btn"
                            onClick={() => handleRespond(cons.id)}
                            className="px-4 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all font-body"
                          >
                            إرسال
                          </button>
                          <button
                            data-testid="cancel-response-btn"
                            onClick={() => {
                              setSelectedConsultation(null);
                              setResponseText('');
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-body"
                          >
                            إلغاء
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-body whitespace-nowrap ${
                    cons.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  {cons.status === 'pending' ? 'معلقة' : 'تم الرد'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {activeTab === 'registered' && consultations.length === 0 && (
          <div className="text-center py-16" data-testid="no-consultations">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-body">لا توجد استشارات حاليا</p>
          </div>
        )}

        {activeTab === 'guest' && guestConsultations.length === 0 && (
          <div className="text-center py-16" data-testid="no-guest-consultations">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-body">لا توجد استشارات من الضيوف حاليا</p>
          </div>
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4" data-testid="consultation-modal">
            <h2 className="text-2xl font-heading font-bold text-primary mb-6">
              طلب استشارة جديدة
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                data-testid="consultation-subject-input"
                type="text"
                placeholder="موضوع الاستشارة"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
              />
              <textarea
                data-testid="consultation-message-input"
                placeholder="اكتب استفسارك هنا..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={5}
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
              />
              <div className="flex gap-4 justify-end">
                <button
                  data-testid="consultation-cancel-btn"
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-body"
                >
                  إلغاء
                </button>
                <button
                  data-testid="consultation-submit-btn"
                  type="submit"
                  className="px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all font-body"
                >
                  إرسال
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
